import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import NavigationBar from '../../components/ui/NavigationBar';
import Icon from '../../components/AppIcon';

const ProfilePage = () => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [profileData, setProfileData] = useState({
    fullName: '',
    email: '',
    points: 0,
    achievements: []
  });

  useEffect(() => {
    const fetchProfileData = async () => {
      if (!user?.id) {
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        // Fetch user profile
        const { data: profile, error: profileError } = await supabase?.from('user_profiles')?.select('full_name, email, points')?.eq('id', user?.id)?.single();

        if (profileError && !profileError?.message?.includes('PGRST116')) {
          console.error('Error fetching profile:', profileError);
        }

        // Fetch achievements (latest 10)
        const { data: achievements, error: achievementsError } = await supabase?.from('achievements')?.select('id, achievement_id, achievement_title, icon, earned_at')?.eq('user_id', user?.id)?.order('earned_at', { ascending: false })?.limit(10);

        if (achievementsError) {
          console.error('Error fetching achievements:', achievementsError);
        }

        setProfileData({
          fullName: profile?.full_name || '',
          email: profile?.email || '',
          points: profile?.points || 0,
          achievements: achievements || []
        });
      } catch (error) {
        console.error('Error loading profile data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProfileData();
  }, [user?.id]);

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <NavigationBar />
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Chargement...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <NavigationBar />
      <div className="max-w-4xl mx-auto px-4 py-8 mt-16">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Mon profil</h1>
        </div>

        {/* Profile Info Section */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Informations</h2>
          <div className="space-y-3">
            {profileData?.fullName && (
              <div>
                <span className="text-sm text-gray-500">Nom complet</span>
                <p className="text-gray-900 font-medium">{profileData?.fullName}</p>
              </div>
            )}
            {profileData?.email && (
              <div>
                <span className="text-sm text-gray-500">Email</span>
                <p className="text-gray-900 font-medium">{profileData?.email}</p>
              </div>
            )}
          </div>
        </div>

        {/* Points Section */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Mes points</h2>
          <div className="flex items-center gap-3">
            <div className="bg-blue-100 rounded-full p-3">
              <Icon name="star" className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-3xl font-bold text-blue-600">{profileData?.points}</p>
              <p className="text-sm text-gray-500">Points d'exploration</p>
            </div>
          </div>
        </div>

        {/* Badges Section */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Mes badges</h2>
          {profileData?.achievements?.length > 0 ? (
            <div className="space-y-3">
              {profileData?.achievements?.map((achievement) => (
                <div
                  key={achievement?.id}
                  className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <div className="text-3xl">{achievement?.icon || '🏅'}</div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900">{achievement?.achievement_title}</h3>
                    <p className="text-sm text-gray-500">
                      Obtenu le {new Date(achievement?.earned_at)?.toLocaleDateString('fr-FR', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric'
                      })}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <p>Aucun badge obtenu pour le moment</p>
              <p className="text-sm mt-2">Complétez des quiz pour débloquer des badges !</p>
            </div>
          )}
        </div>

        {/* Sign Out Section */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <button
            onClick={handleSignOut}
            className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2"
          >
            <Icon name="log-out" className="w-5 h-5" />
            Déconnexion
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;